import { useAnalytics } from '#~/analytics/hooks/index.js';
import type { IdentifiedBy } from '#~/analytics/domains/device-identification.js';
import {
  createDeviceLifecycleTracker,
  isEmbeddedDiscovery,
  resolveEmbeddedFlowProps
} from '#~/analytics/domains/index.js';
import { CameraModelTooltip } from '#~/common/tooltip/camera-model.jsx';
import { Brand, Model } from '#~/device/components/config/index.js';
import { useCatalogueModels } from '#~/device/registry/catalogue.js';
import { useBrands, useModels, useTemplate } from '#~/device/registry/hooks.js';
// Direct module import: step suites mock the config barrel with Brand/Model only.
// eslint-disable-next-line import/no-internal-modules
import {
  NotListedBrandLabel,
  ReadOnlyValue
} from '#~/device/components/config/read-only-value.jsx';
import { Sel, STag } from '#~/stag/tag.js';
import { Column, Group } from '@studio/design-system/form';
import { Mediator } from '@studio/design-system/input';
import { Label } from '@studio/design-system/label';
import type { HtmlAttributes } from '@studio/design-system/props';
import { Tag } from '@studio/design-system/tag';
import type { Brand as M_Brand, Model as M_Model } from '@studio/models/device';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, useShallowEffect } from '@studio/react-utils/hooks';
import { toString } from '@tryiris-ai/js-types';
import { shallowEqual } from 'fast-equals';
import { pick } from 'lodash-es';
import type { ReactElement, SyntheticEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { Opaque, RequireAtLeastOne } from 'type-fest';
import { shallow } from 'zustand/shallow';
import {
  isNotListedAnswer,
  markIdentifiedBy,
  openNotListed,
  reopenNotListed
} from '../store/identification.slice.js';
import type { IdentifiedDiscovery } from '../identification/identify-at-host.js';
import { useIdentifyAtHost } from '../identification/identify-at-host.js';
import { cameraHost } from '../source/discovery-transports.js';
import { useProducer, useStore } from '../store/hooks.js';
import type { Selection } from './types.js';
import type { Detected } from './utils.js';
import {
  findBrandIdForModel,
  getDefaultName,
  getDetected,
  getInitialSelection,
  getPickedIdentifiedBy,
  useExternalModelId
} from './utils.js';

/**
 * Field label with an optional "Detected" pill after it (Board A).
 *
 * Per rule: ui-architecture (ai.knowledge/global) — the pill is the design-system
 * `Tag` with the theme's success tone, so Iris and Hive share one component.
 * Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — the pill
 * carries `detected-brand` / `detected-model` STags for e2e.
 */
const FieldLabel = ({
  detected,
  field,
  label
}: {
  detected: boolean;
  field: 'brand' | 'model';
  label: string;
}): ReactElement => (
  <Label className='items-center gap-8'>
    {label}
    {detected && (
      <Tag
        className='px-8 py-2 text-xs font-medium'
        message='Detected'
        severity='success'
        {...STag(Sel.ConfigVideoDeviceBrandModel, `detected-${field}`)}
      />
    )}
  </Label>
);

/** Manufacturer `<Details>` component. */
export const Details = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();
  const { track } = useAnalytics();
  const deviceLifecycleTracker = createDeviceLifecycleTracker(track);

  const store = useStore(
    (µ) => ({
      ...pick(µ.candidate, ['modelId', 'name']),
      ...pick(µ.manufacturer, ['defaultName', 'preferredModelId'])
    }),
    shallow
  );

  // ENG-4832 C3 inputs: how the camera was identified and what was typed into
  // "My camera isn't listed".
  const identification = useStore(
    (µ) => pick(µ.identification, ['identifiedBy', 'typedBrand', 'typedModel']),
    shallow
  );

  // Per ENG-3960 §1.7 — the embedded flow reuses these shared brand/model select
  // emits, so stamp the additive is_embedded + linking_surface cohort when the
  // in-flight discovery is embedded ({} otherwise — no double-fire, generic unchanged).
  const sourceSelection = useStore((µ) => µ.source.selection);
  const seededBrandId = useStore((µ) => µ.source.seededBrandId);
  const embeddedFlowProps = resolveEmbeddedFlowProps(isEmbeddedDiscovery(sourceSelection));

  const config = useTemplate(store.modelId);

  const [selection, setSelection] = useState<Selection>(() =>
    getInitialSelection(config, seededBrandId)
  );

  // ENG-4830: what discovery had already resolved when the step opened. A
  // snapshot, because this step writes `candidate.modelId` itself as soon as
  // the operator picks a model — reading it live would turn every search pick
  // into a "Detected" one.
  // ENG-4940: an identify-at-address hit on this step IS a detection, so it
  // re-snapshots `entry` (its only other writer) and the pills follow.
  const [entry, setEntry] = useState(() => ({
    identifiedBy: identification.identifiedBy,
    modelId: store.modelId,
    seededBrandId
  }));
  const [changed, setChanged] = useState<Partial<Detected>>({});
  const entryDetected = getDetected(entry);
  const detected = getDetected(entry, changed);

  const brands = useBrands();
  const models = useModels(selection.brandId);

  // ENG-4831 B3: cached models per brand, so a model-name query in the brand
  // field can offer "Brand · Model" rows. The catalogue is warmed only once the
  // user types in the brand field (nothing beyond brands/selected models/template
  // is fetched on mount); the depot slice is referentially stable between writes,
  // which is what the memoised <Brand> compares.
  const [brandQuery, setBrandQuery] = useState('');
  const modelsByBrand = useCatalogueModels(brands, brandQuery);

  // ENG-4832: the not-listed flow writes `candidate.modelId` itself (Continue →
  // generic Other/Other model, "Did you mean" → the picked model). Follow it so
  // the local selection — and with it `useModels` / the brand field — shows
  // the model the store will save. `config` already follows the store id.
  const markModelWritten = useExternalModelId(store.modelId, (modelId) => {
    setSelection((prev) => ({
      brandId: findBrandIdForModel(modelsByBrand, modelId) ?? prev.brandId,
      modelId
    }));
    // A value the user chose elsewhere is no longer what discovery detected.
    setChanged({ brand: true, model: true });
  });

  // Brand of an externally written model that was not in a loaded collection:
  // take it from the template once that resolves (only while the model matches).
  useEffect(() => {
    const brandId = config?.model.brandId;

    if (config?.model.id === selection.modelId && brandId && brandId !== selection.brandId) {
      setSelection((prev) => ({ ...prev, brandId }));
    }
  }, [config, selection.modelId]);

  const setIdentifiedBy = useLatestFn((identifiedBy: IdentifiedBy) => {
    producer((draft) => markIdentifiedBy(draft, identifiedBy));
  });

  // C3's Change reopens the screen that produced the answer (C1 or C2) with
  // the typed text kept (ENG-4832 / ENG-4924).
  const handleNotListed = useLatestFn(() => {
    producer((draft) => reopenNotListed(draft));
  });

  // ENG-4924: a "Not yet supported" row is never selected. It opens C2
  // ("<Brand model> isn't supported yet"), whose Continue commits the generic
  // model and records `not_yet_supported_picked`; Back / Escape leave the
  // previous selection untouched. Returns whether the pick was diverted.
  const reportNotYetSupported = (model: M_Model): boolean => {
    if (!model.notYetSupported) return false;

    producer((draft) =>
      openNotListed(
        draft,
        'c2',
        { brand: brands?.items[model.brandId]?.name, model: model.name },
        'not_yet_supported_pick'
      )
    );

    return true;
  };

  const syncSelection = useLatestFn((µ: RequireAtLeastOne<Selection>) => {
    const nextSelection = { ...selection, ...µ };

    if (nextSelection.brandId === 'none') {
      nextSelection.brandId = undefined;
    }

    if (nextSelection.modelId === 'none') {
      nextSelection.modelId = undefined;
    }

    // Did the selection actually change?
    if (shallowEqual(selection, nextSelection)) {
      return;
    }

    setSelection(nextSelection);
    // Our own write: must not come back as an "external" change.
    markModelWritten(nextSelection.modelId);

    producer((draft) => {
      draft.candidate.modelId = nextSelection.modelId;

      // if we have control settings for this model, we need to clear them.
      draft.candidate.control = undefined;
    });
  });

  // React to changes in the configuration.
  useEffect(() => {
    producer(({ manufacturer }) => {
      manufacturer.config = config;
    });
  }, [config]);

  // React to changes to the selection.
  useShallowEffect(() => {
    producer(({ manufacturer }) => {
      // Keep track of the default device name.
      manufacturer.defaultName = getDefaultName({ brands, models, selection });

      // Keep track of the preferred model for each brand.
      if (selection.brandId && selection.modelId) {
        manufacturer.preferredModelId[selection.brandId] = selection.modelId;
      }
    });
  }, [selection, brands, models]);

  const trackBrand = useLatestFn((brandId: M_Brand['id']) => {
    const selectedBrand = brands?.items?.[brandId];
    if (selectedBrand) {
      // Per ENG-3960 §E12 — device_brand is off-convention; use canonical brand_name.
      deviceLifecycleTracker.trackDeviceSelectBrand({
        brand_name: selectedBrand.name,
        device_brand_id: brandId.toString(),
        ...embeddedFlowProps
      });
    }
  });

  const trackModel = useLatestFn((model: M_Model, brandId: M_Brand['id'] | undefined) => {
    const selectedBrand = brandId ? brands?.items?.[brandId] : undefined;
    // Per ENG-3960 §E12 — device_model/device_brand are off-convention; use canonical *_name.
    deviceLifecycleTracker.trackDeviceSelectModel({
      model_name: model.name,
      device_model_id: model.id.toString(),
      ...(brandId ? { device_brand_id: brandId.toString() } : {}),
      ...(selectedBrand?.name ? { brand_name: selectedBrand.name } : {}),
      ...embeddedFlowProps
    });
  });

  /**
   * Drop the pills of the fields the operator just changed. Returns whether a
   * detected value was replaced (ENG-4833: a replaced detection is reported as
   * a plain `searched`; the ticket has no separate bucket for it).
   */
  const clearDetected = useLatestFn((fields: (keyof Detected)[]): boolean => {
    const replaced = fields.some((field) => detected[field]);

    if (replaced) {
      setChanged((µ) => ({ ...µ, ...Object.fromEntries(fields.map((f) => [f, true])) }));
    }

    return replaced;
  });

  const handleBrandChange = useLatestFn((brandId: M_Brand['id']) => {
    syncSelection({ brandId, modelId: store.preferredModelId[brandId] });
    trackBrand(brandId);

    // A different brand also invalidates the detected model.
    if (clearDetected(['brand', 'model'])) {
      setIdentifiedBy('searched');
    }
  });

  /** Commit a model without recording an identification answer (auto-picks). */
  const selectModel = useLatestFn((modelId: M_Model['id']) => {
    syncSelection({ modelId });

    const selectedModel = models?.items?.[modelId];
    if (selectedModel) {
      trackModel(selectedModel, selection.brandId);
    }
  });

  const handleModelChange = useLatestFn((modelId: M_Model['id']) => {
    const selectedModel = models?.items?.[modelId];
    if (selectedModel && reportNotYetSupported(selectedModel)) return;

    selectModel(modelId);

    if (selectedModel) {
      // ENG-4833: read the detected state before the pick clears it — a brand
      // still detected with no detected model is `brand_detected_then_searched`.
      const brandOnlyDetected = detected.brand && !detected.model;
      clearDetected(['model']);
      setIdentifiedBy(getPickedIdentifiedBy(brandOnlyDetected));
    }
  });

  // ENG-4831 B3: a combined "Brand · Model" row commits both ids in one update.
  const handleCombinedChange = useLatestFn((model: M_Model) => {
    if (reportNotYetSupported(model)) return;

    syncSelection({ brandId: model.brandId, modelId: model.id });
    trackBrand(model.brandId);
    trackModel(model, model.brandId);

    const fields: (keyof Detected)[] =
      model.brandId === selection.brandId ? ['model'] : ['brand', 'model'];
    // ENG-4833: only a pick inside the detected brand counts as
    // brand_detected_then_searched; a different brand is a plain search.
    const brandOnlyDetected = fields.length === 1 && detected.brand && !detected.model;
    clearDetected(fields);
    setIdentifiedBy(getPickedIdentifiedBy(brandOnlyDetected));
  });

  const handleNameChange = useLatestFn((nextValue: string, event: SyntheticEvent) => {
    const nextName = toString(nextValue);

    if (nextName && nextName !== store.name) {
      producer((draft) => {
        draft.candidate.name = nextName;
      });
    } else {
      event.preventDefault();
    }
  });
  const selectedBrand = Object.entries(brands?.items ?? {})?.find?.(
    ([, { id }]) => id === selection.brandId
  );
  const isOtherBrandSelected = selectedBrand?.[1]?.name.toLowerCase().includes('other');

  // ENG-4831 B5: the pickers' "My camera isn't listed" action hands over the
  // text the user already typed, so C1 opens prefilled instead of blank. The
  // model action also carries the selected brand's name — unless that is the
  // catch-all "Other" brand, which is not a brand the user meant to type.
  // Per rule: think-in-systems (ai.knowledge/global) — the seed is applied by
  // the slice, so the RTSP step's identical action shares the one rule.
  const seedBrandName = isOtherBrandSelected ? undefined : selectedBrand?.[1]?.name;

  const handleBrandNotListed = useLatestFn((query: string) => {
    producer((draft) => openNotListed(draft, 'c1', { brand: query }));
  });

  const handleModelNotListed = useLatestFn((query: string) => {
    producer((draft) => openNotListed(draft, 'c1', { brand: seedBrandName, model: query }));
  });

  useEffect(() => {
    if (!isOtherBrandSelected) {
      return;
    }

    const otherModel = Object.entries(models?.items ?? {})
      .map(([, model]) => model)
      ?.find(({ name }) => name.toLowerCase().includes('other'));

    if (otherModel?.id) {
      // The generic brand has one model; picking it is not an identification answer.
      selectModel(otherModel.id);
    }
  }, [isOtherBrandSelected, models, brands]);

  // ENG-4940: identify at address. A discovered camera that reaches this step
  // (an NDI or other non-RTSP row with no model) has an address, so its Bridge
  // probes that one host as the step opens. Same rule as the RTSP step
  // (video/rtsp/rtsp.tsx): "unidentified" means no brand picked, or the
  // catch-all Brand="Other", and no not-listed answer; the gate is re-read
  // when the answer lands, so a pick made while the Bridge probes wins.
  const agentId = useStore((µ) => µ.agentId);
  const seededHost = useStore((µ) => µ.source.seededHost);
  const identifyAtHost = useIdentifyAtHost();
  const cameraUnidentified =
    !isNotListedAnswer(identification.identifiedBy) &&
    (!selection.brandId || !!isOtherBrandSelected);

  const applyIdentifiedRow = useLatestFn((row: IdentifiedDiscovery) => {
    if (!cameraUnidentified) return;

    // Same writer as a combined "Brand · Model" pick. A model-level match
    // carries no brandId; the template-brand effect above sets the model's own
    // brand once its template resolves.
    syncSelection({ brandId: row.brandId, modelId: row.modelId });
    setEntry({ identifiedBy: 'detected', modelId: row.modelId, seededBrandId });
    setChanged({});
    setIdentifiedBy('detected');
  });

  useEffect(() => {
    // Discovery already named a model or a brand: nothing to identify.
    if (store.modelId || seededBrandId || !cameraUnidentified) return;

    const host = (sourceSelection ? cameraHost(sourceSelection) : undefined) ?? seededHost;
    if (!host || !agentId) return;

    // Timeout and cancel resolve `undefined` — nothing changes (never guess).
    void identifyAtHost(agentId, host).then((row) => {
      if (row) applyIdentifiedRow(row);
    });
  }, []);

  const memoizedSelection = useMemo(
    () => toAccessor(selection.brandId, handleBrandChange),
    [selection.brandId, handleBrandChange]
  );

  const memoizedModelSelection = useMemo(
    () => toAccessor(selection.modelId as Opaque<string, '__deviceModelId'>, handleModelChange),
    [selection.modelId, handleModelChange]
  );

  // ENG-4832 C3: the operator typed a camera that is not in the catalogue, or
  // confirmed a not-yet-supported one on C2 (ENG-4924). "Change" reopens the
  // not-listed flow (which keeps the typed text) rather than resetting to
  // search, so editing a typo is one click.
  const isNotListedTyped = isNotListedAnswer(identification.identifiedBy);
  const typedModel = identification.typedModel?.trim();

  return (
    <Column {...props}>
      {isNotListedTyped ? (
        <>
          <Group label='Camera brand'>
            <ReadOnlyValue value={NotListedBrandLabel} />
          </Group>

          <Group label='Camera model' annotation={<CameraModelTooltip />}>
            <ReadOnlyValue onChange={handleNotListed} value={typedModel || NotListedBrandLabel} />
          </Group>
        </>
      ) : (
        <>
          <Group
            label={<FieldLabel detected={detected.brand} field='brand' label='Camera brand' />}
          >
            <Brand
              brands={brands}
              models={modelsByBrand}
              onModelSelect={handleCombinedChange}
              onNotListed={handleBrandNotListed}
              onQueryChange={setBrandQuery}
              selection={memoizedSelection}
            />
          </Group>

          {(selectedBrand ? !isOtherBrandSelected : true) && (
            <Group
              label={<FieldLabel detected={detected.model} field='model' label='Camera model' />}
              annotation={<CameraModelTooltip />}
            >
              <Model
                key={selection.brandId}
                brandId={selection.brandId}
                brandName={selectedBrand?.[1]?.name}
                // A2 (ENG-4831): brand detected, model still to find — start in
                // the model search with the list already open to pick from.
                focusOnMount={entryDetected.brand && !entryDetected.model}
                models={models}
                onNotListed={handleModelNotListed}
                openOnMount={entryDetected.brand && !entryDetected.model}
                selection={memoizedModelSelection}
              />
            </Group>
          )}
        </>
      )}

      <Group label='Camera nickname'>
        <Mediator onCommit={handleNameChange} placeholder={store.defaultName} value={store.name} />
      </Group>
    </Column>
  );
};
